/**
 * Reading the shape out of the money that never reaches a member.
 *
 * Pure, and in its own file so the component beside it exports only
 * components. Both functions exist to make an absence visible: the years a
 * kind disclosed nothing are the finding, and they are the one thing a
 * stacked bar cannot show, because a missing segment and a very small one
 * look identical.
 */

import type { OtherKind, OtherMoney } from "../api";

/** The kinds, in the order the stack and the legend use. */
export const ORDER: OtherKind[] = ["ple", "pic", "me"];

export type Column = {
  year: number;
  total: number;
  parts: { kind: OtherKind; total: number }[];
};

/**
 * The rows folded into one column per year, in year order.
 *
 * Every kind is present in every column, including the ones that are zero.
 * Dropping them is what turns a four-yearly event into a steady stream.
 */
export function columns(data: OtherMoney): Column[] {
  const years = [...new Set(data.by_year.map((r) => r.filing_year))].sort((a, b) => a - b);
  return years.map((year) => {
    const parts = ORDER.map((kind) => ({
      kind,
      total: data.by_year.find((r) => r.filing_year === year && r.kind === kind)?.total ?? 0,
    }));
    return { year, parts, total: parts.reduce((sum, p) => sum + p.total, 0) };
  });
}

/**
 * The years one kind disclosed nothing at all.
 *
 * Empty for a kind present in every year, which has no pulse to report, and
 * for one absent from every year, which is not in the corpus. Only the kinds
 * that come and go get a sentence.
 */
export function silentYears(data: OtherMoney, kind: OtherKind): number[] {
  const rows = data.by_year.filter((r) => r.kind === kind);
  const silent = rows.filter((r) => r.total === 0).map((r) => r.filing_year);
  return silent.length && silent.length < rows.length ? silent : [];
}

/** "2022, 2023 and 2026", as the rest of the site writes a list. */
export function listed(years: number[]): string {
  if (years.length === 1) return String(years[0]);
  return `${years.slice(0, -1).join(", ")} and ${years[years.length - 1]}`;
}
